import { AudienceFooter } from "@/components/audience-page/AudienceFooter";
import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import beamLogo from "@/assets/beam-logo.png";
import { SEOHead } from "@/components/SEOHead";
import { CommunityHospitalsModal } from "@/components/CommunityHospitalsModal";
import { SolutionsMenu, SolutionsMenuMobile } from "@/components/SolutionsMenu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { SearchTrigger } from "@/components/dex/SearchTrigger";


const mainPaths = [
  {
    label: "Solo Provider",
    subtitle: "Physician, NP, or PA",
    pain: "Get specialist input without losing the patient. Transparent pricing, no long contracts, live in days.",
    href: "/individual",
    num: "01",
  },
  {
    label: "Groups & Clinics",
    subtitle: "Primary Care, Urgent Care, & ER",
    pain: "Give every provider on your team reliable specialty access. Keep patients in your ecosystem.",
    href: "/groups",
    num: "02",
  },
  {
    label: "PACE Organizations",
    subtitle: "Capitated Care Programs",
    pain: "Cut outside referrals and avoidable hospitalizations. Keep participants in the community — and in your cap.",
    href: "/groups/pace",
    num: "03",
  },
  {
    label: "Community Hospitals",
    subtitle: "Micro and Rural",
    pain: "Reduce leakage, improve documentation, and capture revenue you're already generating. One team, fully integrated.",
    href: "/inpatient-revenue-framework",
    num: "04",
  },
  {
    label: "Occupational Health",
    subtitle: "Employee Health Programs",
    pain: "Expand your clinical service offering to employees with input from specialists and avoid unnecessary referrals and wait times.",
    href: "/enterprise/occupational-health",
    num: "06",
  },
];

const slimPaths = [
  {
    label: "RHTP Grants",
    subtitle: "",
    pain: "Federal funding to build this infrastructure. Most systems leave it on the table.",
    href: "/rural-health-transformation-program-rhtp",
    num: "05",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.5 + i * 0.1, ease: [0.22, 1, 0.36, 1] as const },
  }),
};

const Index = () => {
  const [hospitalModalOpen, setHospitalModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen text-foreground overflow-x-hidden flex flex-col" style={{ background: 'linear-gradient(160deg, hsl(252 35% 15%), hsl(264 45% 20%))' }}>
      <CommunityHospitalsModal open={hospitalModalOpen} onClose={() => setHospitalModalOpen(false)} />
      <SEOHead
        title="Beam Healthcare — Specialist Curbside Consults"
        description="Whether you're a solo provider, a growing group, or a full hospital system — get instant specialist access to keep patients in your care."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Beam Healthcare",
          "url": "https://solutions.beam.healthcare/",
          "description": "Specialist curbside consults for primary care providers. Same-day answers from board-certified specialists."
        }}
      />
      {/* NAV */}
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-50 w-full"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 px-6 py-2.5">
          <Link to="/">
            <motion.img
              src={beamLogo}
              alt="Beam Healthcare"
              className="h-[3.6rem] md:h-[4.8rem] brightness-0 invert"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            />
          </Link>
          <div className="hidden md:flex items-center gap-5">
            <SolutionsMenu onDark />
          </div>
          <div className="md:hidden flex items-center gap-2">
            <SearchTrigger className="text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground" />
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger
                aria-label="Open menu"
                className="inline-flex items-center justify-center h-10 w-10 rounded-full text-white hover:bg-white/10 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </SheetTrigger>

              <SheetContent side="right" className="w-72">
                <nav className="mt-10 flex flex-col">
                  <SolutionsMenuMobile onNavigate={() => setMenuOpen(false)} />
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </motion.nav>

      {/* HERO */}
      <main className="relative flex-1 flex items-center justify-center overflow-hidden px-6 pb-20 pt-14 md:pt-0">
        
        <section className="contents">

        <div className="relative z-10 max-w-5xl mx-auto w-full">
          {/* Headline */}
          <div className="text-center mb-16 md:mb-20">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-[4.25rem] leading-[1.08] mb-6 text-white tracking-tight"
            >
              Stop Losing Patients{" "}
              <br className="hidden sm:block" />
              to the{" "}
              <span className="text-primary">
                Next System Over.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-white/70 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto tracking-[-0.01em]"
            >
              Get started in days. Keep more care local.
              <br />
              Capture more of the value you're already creating.
            </motion.p>

          </div>

          {/* 2x2 Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {mainPaths.map((path, i) => {
              const isHospitalCard = path.href === "/inpatient-revenue-framework";

              const icons: Record<string, React.ReactNode> = {
                "01": <svg className="w-9 h-9 text-foreground/80 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" /></svg>,
                "02": <svg className="w-9 h-9 text-foreground/80 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0ZM15.75 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM2.25 9.75a3 3 0 1 1 6 0 3 3 0 0 1-6 0ZM6.31 17.243a11.23 11.23 0 0 1 11.378 0c.522.32.863.89.863 1.507V20.25h-13.5v-1.5c0-.617.34-1.187.862-1.507ZM1.5 17.748a10.72 10.72 0 0 1 3.042-.874c-.26.463-.412.996-.412 1.565v1.561H1.5v-2.252Zm21 0v2.252h-2.63v-1.561c0-.57-.152-1.102-.412-1.565a10.72 10.72 0 0 1 3.042.874Z" clipRule="evenodd" /></svg>,
                "03": <svg className="w-9 h-9 text-foreground/80 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z" /></svg>,
                "04": <svg className="w-9 h-9 text-foreground/80 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M4.5 2.25a.75.75 0 0 0 0 1.5v16.5h-.75a.75.75 0 0 0 0 1.5h16.5a.75.75 0 0 0 0-1.5h-.75V3.75a.75.75 0 0 0 0-1.5h-15ZM9 6a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5H9Zm-.75 3.75A.75.75 0 0 1 9 9h1.5a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75ZM9 12a.75.75 0 0 0 0 1.5h1.5a.75.75 0 0 0 0-1.5H9Zm3.75-5.25A.75.75 0 0 1 13.5 6H15a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75ZM13.5 9a.75.75 0 0 0 0 1.5H15a.75.75 0 0 0 0-1.5h-1.5Zm-.75 3.75a.75.75 0 0 1 .75-.75H15a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1-.75-.75ZM9 19.5v-2.25a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 .75.75v2.25H9Z" clipRule="evenodd" /></svg>,
                "06": <svg className="w-9 h-9 text-foreground/80 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" d="M7.5 5.25a3 3 0 0 1 3-3h3a3 3 0 0 1 3 3v.205c.933.085 1.857.197 2.774.334 1.454.218 2.476 1.483 2.476 2.917v3.033c0 1.211-.734 2.352-1.936 2.752A24.726 24.726 0 0 1 12 15.75c-2.73 0-5.357-.442-7.814-1.259-1.202-.4-1.936-1.541-1.936-2.752V8.706c0-1.434 1.022-2.7 2.476-2.917A48.814 48.814 0 0 1 7.5 5.455V5.25Zm7.5 0v.09a49.488 49.488 0 0 0-6 0v-.09a1.5 1.5 0 0 1 1.5-1.5h3a1.5 1.5 0 0 1 1.5 1.5Zm-3 8.25a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" clipRule="evenodd" /><path d="M3 18.4v-2.796a4.3 4.3 0 0 0 .713.31A26.226 26.226 0 0 0 12 17.25c2.892 0 5.68-.468 8.287-1.335.252-.084.49-.189.713-.311V18.4c0 1.452-1.047 2.728-2.523 2.923-2.12.282-4.282.427-6.477.427a49.19 49.19 0 0 1-6.477-.427C4.047 21.128 3 19.852 3 18.4Z" /></svg>,
              };

              const headerAndDesc = (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    {icons[path.num]}
                    <div>
                      <h2 className="font-heading font-bold text-lg text-foreground tracking-tight leading-snug">
                        {path.label}
                      </h2>
                      {path.subtitle && (
                        <span className="text-[13px] text-foreground/70 font-medium">{path.subtitle}</span>
                      )}
                    </div>
                  </div>
                  <p className="text-foreground/70 text-[15px] leading-[1.65] flex-1">
                    {path.pain}
                  </p>
                </>
              );


              const cardClass =
                "group relative flex flex-col bg-white rounded-2xl p-6 md:p-7 hover:shadow-[0_16px_48px_-12px_hsl(264_55%_52%/0.3)] transition-all duration-500 cursor-pointer h-full w-full text-left";

              return (
                <motion.div
                  key={path.href}
                  custom={i}
                  variants={cardVariants}
                  initial="hidden"
                  animate="visible"
                  whileHover={{ y: -4, transition: { duration: 0.3, ease: "easeOut" } }}
                >
                  {isHospitalCard ? (
                    <button onClick={() => setHospitalModalOpen(true)} className={cardClass}>
                      {headerAndDesc}
                    </button>
                  ) : (
                    <Link to={path.href} className={cardClass}>
                      {headerAndDesc}
                    </Link>
                  )}

                </motion.div>
              );
            })}
          </div>

          {/* Slim specialized programs strip */}
          <div className="mt-4 max-w-4xl mx-auto">
            {slimPaths.map((path, i) => (
              <motion.div
                key={path.href}
                custom={4 + i}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                whileHover={{ y: -3, transition: { duration: 0.3, ease: "easeOut" } }}
              >
                <Link
                  to={path.href}
                  className="group flex items-center gap-4 bg-white rounded-2xl px-6 py-4 hover:shadow-[0_16px_48px_-12px_hsl(264_55%_52%/0.3)] transition-all duration-500 cursor-pointer"
                >
                  <svg className="w-7 h-7 text-foreground/80 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 0 1-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004ZM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 0 1-.921.42Z" />
                    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v.816a3.836 3.836 0 0 0-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 0 1-.921-.421l-.879-.66a.75.75 0 0 0-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 0 0 1.5 0v-.81a4.124 4.124 0 0 0 1.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 0 0-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 0 0 .933-1.175l-.415-.33a3.836 3.836 0 0 0-1.719-.755V6Z" clipRule="evenodd" />
                  </svg>
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3 shrink-0">
                    <h2 className="font-heading font-bold text-base text-foreground tracking-tight leading-snug">
                      {path.label}
                    </h2>
                    {path.subtitle && <span className="text-[13px] text-foreground/70 font-medium">{path.subtitle}</span>}
                  </div>
                  <p className="hidden md:block text-foreground/70 text-[13px] leading-snug flex-1">
                    {path.pain}
                  </p>
                  <span className="text-foreground/60 group-hover:text-primary group-hover:translate-x-1 transition-all text-xl shrink-0 ml-auto md:ml-0" aria-hidden="true">→</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      </main>

      {/* FOOTER */}
      <AudienceFooter />
    </div>
  );
};

export default Index;
